// Grouped client for Entity operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { Entity } from '../models/Entity';
import { FilterEntityRequest } from '../models/FilterEntityRequest';
import { DataviewEntityResponse } from '../models/DataviewEntityResponse';
import { FormSerializer } from '../serializers/FormSerializer';
import { EntitySerializer } from '../serializers/EntitySerializer';
import { FilterEntityRequestSerializer } from '../serializers/FilterEntityRequestSerializer';
import { DataviewEntityResponseSerializer } from '../serializers/DataviewEntityResponseSerializer';

/**
 * HTTP client for Entity operations
 *
 * Client for Entity operations
 */
export class EntityClient {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  async getFormForEntity(formName: string, objectId: string, name?: string | null, description?: string | null, id?: string | null, t?: string | null, email?: string | null, phone?: string | null, timeZoneId?: string | null, organizationId?: string | null, isActive?: boolean | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/Entity({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (t !== undefined) {
      queryParams.append('_t', String(t));
    }
    if (email !== undefined) {
      queryParams.append('Email', String(email));
    }
    if (phone !== undefined) {
      queryParams.append('Phone', String(phone));
    }
    if (timeZoneId !== undefined) {
      queryParams.append('TimeZoneId', String(timeZoneId));
    }
    if (organizationId !== undefined) {
      queryParams.append('OrganizationId', String(organizationId));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeEntity(formName: string, name?: string | null, description?: string | null, id?: string | null, t?: string | null, email?: string | null, phone?: string | null, timeZoneId?: string | null, organizationId?: string | null, isActive?: boolean | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/Entity/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (t !== undefined) {
      queryParams.append('_t', String(t));
    }
    if (email !== undefined) {
      queryParams.append('Email', String(email));
    }
    if (phone !== undefined) {
      queryParams.append('Phone', String(phone));
    }
    if (timeZoneId !== undefined) {
      queryParams.append('TimeZoneId', String(timeZoneId));
    }
    if (organizationId !== undefined) {
      queryParams.append('OrganizationId', String(organizationId));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterEntity(request?: FilterEntityRequest | null, options?: { headers?: Record<string, string> }): Promise<Entity[]> {
    const path = '/app/api/Object/Entity/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterEntityRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => EntitySerializer.fromObject(item));
  }

  async recentEntity(request?: FilterEntityRequest | null, options?: { headers?: Record<string, string> }): Promise<Entity[]> {
    const path = '/app/api/Object/Entity/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterEntityRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => EntitySerializer.fromObject(item));
  }

  async dataViewEntity(request?: FilterEntityRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewEntityResponse> {
    const path = '/app/api/Object/Entity/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterEntityRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewEntityResponseSerializer.fromObject(data);
  }

}
